import { AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { RollingConfigState, SoftwareUpdateWizardState } from "../types";

interface Props {
  state: SoftwareUpdateWizardState;
  onChange: (patch: Partial<RollingConfigState>) => void;
}

export function RollingForm({ state, onChange }: Props) {
  const r = state.rolling;
  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-xs">
        <AlertTriangle className="h-4 w-4 text-warning mt-0.5" aria-hidden="true" />
        <div>
          <div className="font-medium text-foreground">Rolling activation pending backend</div>
          <div className="text-muted-foreground">
            The Smplify rolling resolver isn't live yet. You can build the policy intent here and
            optionally save it as a one-time Pinned snapshot using the version you provide.
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rolling rules</CardTitle>
          <CardDescription>
            Tell the resolver which version lane to track and when to apply it after release.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-2">
            <Label>Version lane</Label>
            <RadioGroup
              value={r.lane}
              onValueChange={(v) => onChange({ lane: v as RollingConfigState["lane"] })}
              className="grid gap-2 sm:grid-cols-3"
            >
              {(
                [
                  { id: "latest", title: "Latest", desc: "Always the newest approved release." },
                  { id: "n-1", title: "N-1", desc: "Stay one major release behind." },
                  { id: "specific-major", title: "Specific major", desc: "Track one major only." },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.id}
                  htmlFor={`lane-${opt.id}`}
                  className={`rounded-md border p-3 text-sm cursor-pointer transition-colors ${
                    r.lane === opt.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id={`lane-${opt.id}`} value={opt.id} />
                    <span className="font-medium">{opt.title}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{opt.desc}</div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {r.lane === "specific-major" && (
            <div className="grid gap-2 sm:max-w-xs">
              <Label htmlFor="rolling-major">Major version</Label>
              <Input
                id="rolling-major"
                value={r.major}
                onChange={(e) => onChange({ major: e.target.value })}
                placeholder="e.g. 15"
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="rolling-delay">Apply N days after release</Label>
              <Input
                id="rolling-delay"
                type="number"
                min={0}
                max={90}
                value={r.delayDays}
                onChange={(e) => onChange({ delayDays: Math.max(0, Math.min(90, parseInt(e.target.value || "0", 10))) })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="rolling-time">At time (device local)</Label>
              <Input
                id="rolling-time"
                type="time"
                value={r.applyTime}
                onChange={(e) => onChange({ applyTime: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-3">
            <Label>Release types</Label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={r.includeMinor}
                onCheckedChange={(v) => onChange({ includeMinor: v === true })}
              />
              Minor releases (recommended)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={r.includeMajor}
                onCheckedChange={(v) => onChange({ includeMajor: v === true })}
              />
              Major releases (e.g. macOS 15 → 16)
            </label>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="rolling-snapshot">Snapshot version for interim save (optional)</Label>
            <Input
              id="rolling-snapshot"
              value={r.snapshotVersion}
              placeholder="e.g. 15.5"
              onChange={(e) => onChange({ snapshotVersion: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              If provided, the wizard writes a one-time Pinned enforcement using this version.
              Until the rolling resolver ships, only the snapshot is enforced.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
